import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { useFetch, Field } from "@/lib/ui";
import ExamTaker from "@/components/ExamTaker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Cross, CheckCircle2 } from "lucide-react";

const GRADES = ["CP1", "CP2", "CE1", "CE2", "CM1", "CM2", "7ème Année Fondamentale", "8ème Année Fondamentale", "9ème Année Fondamentale", "NS1", "NS2", "NS3", "NS4"];

export default function Admission() {
  const { L } = useLang();
  const navigate = useNavigate();
  const [cand, setCand] = useState({ candidate_name: "", candidate_email: "", grade_level: "" });
  const [step, setStep] = useState(1);
  const [exams, setExams] = useState([]);
  const [exam, setExam] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const proceed = async () => {
    if (!cand.candidate_name || !cand.grade_level) { toast.error(L("Enter your name and class", "Entrez votre nom et la classe")); return; }
    try {
      const list = await api.get("/admission/exams", { params: { grade_level: cand.grade_level } });
      setExams(list); setStep(2);
      if (list.length === 0) toast.info(L("No admission exam available for this class yet.", "Aucun examen d'admission disponible pour cette classe."));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const start = async (e) => { const full = await api.get(`/admission/exams/${e.id}`); setExam(full); setStep(3); };
  const submit = async (answers) => {
    setSubmitting(true);
    try { const r = await api.post(`/admission/exams/${exam.id}/submit`, { ...cand, answers }); setDone(r); setStep(4); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center"><Cross className="h-5 w-5 text-primary" /></div>
            <div><p className="font-heading font-extrabold text-sm">ICDH · {L("Admission Exam", "Examen d'admission")}</p><p className="text-[11px] text-primary-foreground/60">Institution Chrétienne D'Haïti</p></div>
          </div>
          <Button size="sm" variant="ghost" className="text-primary-foreground hover:bg-white/10" onClick={() => navigate("/")}>{L("Exit", "Quitter")}</Button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto p-4 sm:p-8">
        {step === 1 && (
          <div className="max-w-md mx-auto animate-fade-up">
            <h1 className="font-heading font-extrabold text-2xl tracking-tight mb-1">{L("Admission Exam", "Examen d'admission")}</h1>
            <p className="text-muted-foreground mb-6">{L("No password needed. Enter your details and select the class you are applying for.", "Aucun mot de passe requis. Entrez vos informations et choisissez la classe demandée.")}</p>
            <div className="space-y-4 bg-card border border-border rounded-lg p-6">
              <Field label={L("Full name", "Nom complet")}><Input value={cand.candidate_name} onChange={(e) => setCand({ ...cand, candidate_name: e.target.value })} data-testid="cand-name-input" /></Field>
              <Field label={L("Email (optional)", "E-mail (optionnel)")}><Input value={cand.candidate_email} onChange={(e) => setCand({ ...cand, candidate_email: e.target.value })} data-testid="cand-email-input" /></Field>
              <Field label={L("Class applying for", "Classe demandée")}>
                <Select value={cand.grade_level} onValueChange={(v) => setCand({ ...cand, grade_level: v })}><SelectTrigger data-testid="cand-grade-select"><SelectValue placeholder={L("Select class", "Choisir la classe")} /></SelectTrigger>
                  <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select>
              </Field>
              <p className="text-xs text-muted-foreground">{L("Note: Chez-Kids kindergarten has no admission exam.", "Note : la section Chez-Kids n'a pas d'examen d'admission.")}</p>
              <Button className="w-full" onClick={proceed} data-testid="admission-proceed">{L("Continue", "Continuer")}</Button>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="animate-fade-up">
            <button className="text-sm text-primary mb-4" onClick={() => setStep(1)}>← {L("Back", "Retour")}</button>
            <h1 className="font-heading font-extrabold text-2xl mb-4">{L("Available Exams", "Examens disponibles")} — {cand.grade_level}</h1>
            {exams.length === 0 ? <Empty title={L("No exam available", "Aucun examen disponible")} /> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {exams.map((e) => (
                  <div key={e.id} className="bg-card border border-border rounded-lg p-5" data-testid={`adm-exam-${e.id}`}>
                    <p className="font-heading font-bold text-lg">{e.title}</p>
                    <p className="text-sm text-muted-foreground">{e.subject} · {e.question_count} {L("questions", "questions")}</p>
                    <Button className="mt-4" onClick={() => start(e)} data-testid={`start-adm-${e.id}`}>{L("Start", "Commencer")}</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {step === 3 && exam && (
          <div className="animate-fade-up">
            <h1 className="font-heading font-extrabold text-2xl mb-1">{exam.title}</h1>
            <p className="text-muted-foreground mb-6">{exam.subject} · {cand.candidate_name}</p>
            <ExamTaker exam={exam} onSubmit={submit} submitting={submitting} L={(en) => en} />
          </div>
        )}
        {step === 4 && done && (
          <div className="max-w-md mx-auto text-center animate-fade-up bg-card border border-border rounded-lg p-8" data-testid="admission-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h1 className="font-heading font-extrabold text-2xl mt-4">{L("Thank you!", "Merci !")}</h1>
            <p className="text-muted-foreground mt-2">{L("Your admission exam has been submitted. The school administration will review your results.", "Votre examen d'admission a été soumis. L'administration examinera vos résultats.")}</p>
            <Button className="mt-6" onClick={() => navigate("/")}>{L("Done", "Terminé")}</Button>
          </div>
        )}
      </main>
    </div>
  );
}
